import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	ExternalLink,
	Play,
	RefreshCw,
	RotateCcw,
	Smartphone,
	Target,
	Trash2,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { GpioDebugPanel } from "@/components/timer/GpioDebugPanel";
import { TimerConnectionBar } from "@/components/timer/TimerConnectionBar";
import {
	useTimerDiagnostics,
	type LogEntry,
} from "@/lib/timer/use-timer-diagnostics";
import type { InfoMessage, TimerState } from "@/lib/timer/types";

export const Route = createFileRoute("/admin/hardware-test")({
	component: HardwareTestPage,
});

const STATE_STYLES: Record<TimerState, string> = {
	idle: "bg-muted text-muted-foreground",
	armed: "bg-status-qualifying text-foreground",
	starting: "bg-cherry/70 text-foreground",
	timing: "bg-cherry text-foreground",
	finished: "bg-success text-foreground",
};

const STATE_LABELS: Record<TimerState, string> = {
	idle: "IDLE",
	armed: "ARMED",
	starting: "STARTING",
	timing: "RACING",
	finished: "FINISHED",
};

function HardwareTestPage() {
	const timer = useTimerDiagnostics();
	const isConnected = timer.connectionState === "connected";
	const state = timer.timerState ?? "idle";

	// Persisted settings (hydration-safe: init with stable defaults, sync in useEffect)
	const [laneCount, setLaneCount] = useState(3);
	const [demoMode, setDemoMode] = useState(false);
	const [dnfInput, setDnfInput] = useState("");

	// Restore persisted settings on mount
	useEffect(() => {
		const savedLanes = localStorage.getItem("hw-test-lanes");
		if (savedLanes) setLaneCount(Number(savedLanes));
		const savedDemo = localStorage.getItem("hw-test-demo");
		if (savedDemo === "true") setDemoMode(true);
	}, []);

	// Persist lane count
	useEffect(() => {
		localStorage.setItem("hw-test-lanes", String(laneCount));
	}, [laneCount]);

	// Persist demo mode
	useEffect(() => {
		localStorage.setItem("hw-test-demo", String(demoMode));
	}, [demoMode]);

	// Sync demo mode from device status
	useEffect(() => {
		setDemoMode(timer.isDemo);
	}, [timer.isDemo]);

	// Initialize DNF input from device
	useEffect(() => {
		if (timer.dnfTimeoutMs !== null && dnfInput === "") {
			setDnfInput(String(timer.dnfTimeoutMs));
		}
	}, [timer.dnfTimeoutMs, dnfInput]);

	const [hasBluetooth, setHasBluetooth] = useState(false);
	const [hasSerial, setHasSerial] = useState(false);
	const [hasCamera, setHasCamera] = useState(false);
	useEffect(() => {
		setHasBluetooth("bluetooth" in navigator);
		setHasSerial("serial" in navigator);
		setHasCamera(!!navigator.mediaDevices?.getUserMedia);
	}, []);

	const toggleDemo = useCallback(async () => {
		const next = !demoMode;
		setDemoMode(next);
		if (isConnected) {
			await timer.sendConfig({ demo: next });
		}
	}, [demoMode, isConnected, timer]);

	const applyDnf = useCallback(async () => {
		const val = Number.parseInt(dnfInput, 10);
		if (!Number.isNaN(val) && val > 0 && isConnected) {
			await timer.sendConfig({ dnf_timeout_ms: val });
		}
	}, [dnfInput, isConnected, timer]);

	// Elapsed timer
	const [elapsedMs, setElapsedMs] = useState(0);
	const startTimeRef = useRef<number | null>(null);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (state === "timing") {
			startTimeRef.current = Date.now();
			const tick = () => {
				if (startTimeRef.current) {
					setElapsedMs(Date.now() - startTimeRef.current);
				}
				rafRef.current = requestAnimationFrame(tick);
			};
			rafRef.current = requestAnimationFrame(tick);
		} else {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			if (state === "idle" || state === "armed" || state === "starting") {
				setElapsedMs(0);
				startTimeRef.current = null;
			}
		}
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, [state]);

	return (
		<div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Link
						to="/admin/events"
						className="p-2 hover:bg-secondary rounded-lg transition-colors"
					>
						<ArrowLeft size={20} />
					</Link>
					<h1 className="text-2xl font-heading tracking-wider uppercase">
						Hardware Test
					</h1>
				</div>
				<a
					href="https://flash.pinewood-derby.net"
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
				>
					Firmware Flasher
					<ExternalLink size={14} />
				</a>
			</div>

			{/* Connection Bar */}
			<TimerConnectionBar
				connectionState={timer.connectionState}
				transportName={timer.transportName}
				isDemo={timer.isDemo}
				gateMode={timer.gateMode}
				error={timer.error}
				hasBluetooth={hasBluetooth}
				hasSerial={hasSerial}
				hasCamera={hasCamera}
				onConnectBle={timer.connectBle}
				onConnectSerial={timer.connectSerial}
				onConnectMock={timer.connectMock}
				onConnectCamera={timer.connectCamera}
				onConnectHybridBle={timer.connectHybridBle}
				onConnectHybridSerial={timer.connectHybridSerial}
				onConnectHybridRemoteBle={timer.connectHybridRemoteBle}
				onConnectHybridRemoteSerial={timer.connectHybridRemoteSerial}
				onConnectRemoteCamera={timer.connectRemoteCamera}
				onDisconnect={timer.disconnect}
			/>

			{/* Remote Camera Session */}
			{timer.remoteSessionCode && (
				<RemoteCameraPanel
					sessionCode={timer.remoteSessionCode}
					transport={timer.remoteTransport}
				/>
			)}

			{/* Device Status Panel */}
			<div className="bg-card rounded-xl p-4 space-y-3">
				<h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
					Device Status
				</h2>
				{!isConnected ? (
					<p className="text-sm text-muted-foreground">
						Connect a device to see status
					</p>
				) : (
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
						<StatusItem label="Timer State">
							<span
								className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${STATE_STYLES[state]} ${
									state === "armed" || state === "starting" || state === "timing"
										? "animate-pulse"
										: ""
								}`}
							>
								{STATE_LABELS[state]}
							</span>
						</StatusItem>
						<StatusItem label="Gate Switch">
							{timer.gateClosed !== null ? (
								<span className="flex items-center gap-1.5 text-sm font-bold">
									<span
										className={`w-2.5 h-2.5 rounded-full ${timer.gateClosed ? "bg-success" : "bg-destructive"}`}
									/>
									{timer.gateClosed ? "CLOSED" : "OPEN"}
								</span>
							) : (
								<span className="text-sm text-muted-foreground">
									--
								</span>
							)}
						</StatusItem>
						<StatusItem label="Servo Angle">
							<span className="text-sm font-bold tabular-nums">
								{timer.servoDeg !== null
									? `${timer.servoDeg}°`
									: "--"}
							</span>
						</StatusItem>
						<StatusItem label="Simulated Races">
							<span
								className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${
									timer.isDemo
										? "bg-status-qualifying text-foreground"
										: "bg-muted text-muted-foreground"
								}`}
							>
								{timer.isDemo ? "ON" : "OFF"}
							</span>
						</StatusItem>
						<StatusItem label="DNF Timeout">
							<span className="text-sm font-bold tabular-nums">
								{timer.dnfTimeoutMs !== null
									? `${timer.dnfTimeoutMs}ms`
									: "--"}
							</span>
						</StatusItem>
						<StatusItem label="Lanes">
							<span className="text-sm font-bold tabular-nums">
								{timer.laneCount}
							</span>
						</StatusItem>
					</div>
				)}
			</div>

			{/* Pinout (shown when device reports info) */}
			{timer.deviceInfo && <PinoutPanel info={timer.deviceInfo} />}

			{/* GPIO Debug */}
			{isConnected && (
				<GpioDebugPanel
					isConnected={isConnected}
					gpioDebug={timer.gpioDebug}
					gpioEdgeEvents={timer.gpioEdgeEvents}
					gpioPwm={timer.gpioPwm}
					gpioDigitalLevel={timer.gpioDigitalLevel}
					gpioError={timer.gpioError}
					deviceInfo={timer.deviceInfo}
					timerState={timer.timerState}
					onStart={timer.gpioDebugStart}
					onStop={timer.gpioDebugStop}
				/>
			)}

			{/* Diagnostic Controls */}
			<div className="bg-card rounded-xl p-4 space-y-4">
				<h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
					Controls
				</h2>

				{/* Lane count + Demo + DNF */}
				<div className="flex flex-wrap items-end gap-4">
					<div>
						<label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
							Lanes
						</label>
						<div className="flex gap-1">
							{[1, 2, 3, 4].map((n) => (
								<button
									key={n}
									type="button"
									onClick={() => setLaneCount(n)}
									className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${
										laneCount === n
											? "bg-primary text-primary-foreground"
											: "bg-secondary text-secondary-foreground hover:bg-secondary/80"
									}`}
								>
									{n}
								</button>
							))}
						</div>
					</div>

					<div>
						<label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
							Simulate Races
						</label>
						<button
							type="button"
							onClick={toggleDemo}
							disabled={!isConnected}
							title="When on, the firmware generates fake race times without needing physical cars or sensors"
							className={`px-3 py-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-40 ${
								demoMode
									? "bg-status-qualifying text-foreground"
									: "bg-secondary text-secondary-foreground hover:bg-secondary/80"
							}`}
						>
							{demoMode ? "ON" : "OFF"}
						</button>
					</div>

					<div>
						<label className="block text-xs font-bold text-muted-foreground uppercase mb-1" title="Max time (in milliseconds) before a lane is marked Did Not Finish">
							DNF Timeout (ms)
						</label>
						<div className="flex gap-1">
							<input
								type="number"
								value={dnfInput}
								onChange={(e) => setDnfInput(e.target.value)}
								placeholder="10000"
								className="w-24 px-2 py-2 rounded-lg bg-secondary text-sm text-foreground tabular-nums"
							/>
							<button
								type="button"
								onClick={applyDnf}
								disabled={!isConnected}
								className="px-3 py-2 rounded-lg text-sm font-bold bg-primary text-primary-foreground hover:bg-accent disabled:opacity-40 transition-colors"
							>
								Apply
							</button>
						</div>
					</div>
				</div>

				{/* Action buttons */}
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						onClick={() => timer.arm(laneCount)}
						disabled={!isConnected || state === "starting" || state === "timing"}
						title="Arm the timer — closes the gate servo and waits for the start signal"
						className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-accent disabled:opacity-40 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
					>
						<Target size={16} />
						Arm
					</button>
					<button
						type="button"
						onClick={() => timer.start()}
						disabled={!isConnected || state !== "armed"}
						title="Release the gate servo to start the race (only available when armed)"
						className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-cherry hover:bg-cherry/80 disabled:opacity-40 text-foreground font-bold rounded-lg transition-colors uppercase text-sm"
					>
						<Play size={16} />
						Start
					</button>
					<button
						type="button"
						onClick={() => timer.reset()}
						disabled={!isConnected || state === "idle"}
						title="Return timer to idle state and clear lane results"
						className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-secondary hover:bg-secondary/80 disabled:opacity-40 text-secondary-foreground rounded-lg transition-colors uppercase text-sm"
					>
						<RotateCcw size={16} />
						Reset
					</button>
					<button
						type="button"
						onClick={() => timer.resetAndArm(laneCount)}
						disabled={
							!isConnected ||
							(state !== "timing" && state !== "finished")
						}
						title="Reset and immediately arm for the next race in one step"
						className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-secondary hover:bg-secondary/80 disabled:opacity-40 text-secondary-foreground rounded-lg transition-colors uppercase text-sm"
					>
						<RefreshCw size={16} />
						Reset & Re-Arm
					</button>
				</div>

				{/* Elapsed timer */}
				{isConnected && (
					<div className="text-right">
						<span className="font-display text-4xl text-cherry tabular-nums leading-none">
							{formatElapsed(elapsedMs)}
						</span>
					</div>
				)}
			</div>

			{/* Lane Results */}
			{isConnected && (
				<div className="bg-card rounded-xl p-4 space-y-3">
					<h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
						Lane Results
					</h2>
					<div className="space-y-2">
						{Array.from({ length: laneCount }, (_, i) => i + 1).map(
							(lane) => {
								const result = timer.laneResults.get(lane);
								const place = getPlace(
									lane,
									timer.heatResults,
								);
								return (
									<div
										key={lane}
										className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50"
									>
										<div className="flex items-center gap-3">
											<span className="text-sm font-bold w-16">
												Lane {lane}
											</span>
											{place !== null && (
												<PlaceBadge place={place} />
											)}
										</div>
										<div className="flex items-center gap-2">
											<LaneStatusBadge
												status={
													result?.status ?? "pending"
												}
											/>
											<span className="font-display text-xl tabular-nums w-24 text-right">
												{formatLaneTime(result)}
											</span>
										</div>
									</div>
								);
							},
						)}
					</div>
				</div>
			)}

			{/* Message Log */}
			<MessageLog
				log={timer.messageLog}
				onClear={timer.clearLog}
			/>
		</div>
	);
}

// ── Sub-components ──────────────────────────────────────────────

function StatusItem({
	label,
	children,
}: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">
				{label}
			</p>
			{children}
		</div>
	);
}

function LaneStatusBadge({ status }: { status: "pending" | "valid" | "dnf" }) {
	const styles = {
		pending: "bg-muted text-muted-foreground",
		valid: "bg-success/20 text-success",
		dnf: "bg-destructive/20 text-destructive",
	};
	const labels = { pending: "PENDING", valid: "VALID", dnf: "DNF" };
	return (
		<span
			className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${styles[status]}`}
		>
			{labels[status]}
		</span>
	);
}

function PlaceBadge({ place }: { place: number }) {
	const colors = [
		"bg-podium-gold text-foreground",
		"bg-podium-silver text-foreground",
		"bg-podium-bronze text-foreground",
	];
	const style = colors[place - 1] ?? "bg-muted text-muted-foreground";
	return (
		<span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${style}`}>
			{place === 1 ? "1st" : place === 2 ? "2nd" : place === 3 ? "3rd" : `${place}th`}
		</span>
	);
}

function MessageLog({
	log,
	onClear,
}: { log: LogEntry[]; onClear: () => void }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const shouldAutoScroll = useRef(true);

	const handleScroll = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;
		const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
		shouldAutoScroll.current = atBottom;
	}, []);

	useEffect(() => {
		const el = containerRef.current;
		if (el && shouldAutoScroll.current) {
			el.scrollTop = el.scrollHeight;
		}
	}, [log.length]);

	const directionColors: Record<LogEntry["direction"], string> = {
		sent: "text-blue-400",
		received: "text-green-400",
		system: "text-yellow-400",
	};
	const directionArrows: Record<LogEntry["direction"], string> = {
		sent: "\u2192",
		received: "\u2190",
		system: "\u2022",
	};

	return (
		<div className="bg-card rounded-xl p-4 space-y-2">
			<div className="flex items-center justify-between">
				<h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
					Message Log
					<span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-muted">
						{log.length}
					</span>
				</h2>
				<button
					type="button"
					onClick={onClear}
					className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
				>
					<Trash2 size={12} />
					Clear
				</button>
			</div>
			<div
				ref={containerRef}
				onScroll={handleScroll}
				className="max-h-96 overflow-y-auto font-mono text-xs bg-black/20 rounded-lg p-2 space-y-0.5"
			>
				{log.length === 0 ? (
					<p className="text-muted-foreground text-center py-4">
						No messages yet
					</p>
				) : (
					log.map((entry) => (
						<div
							key={entry.id}
							className={`flex gap-2 ${entry.type === "error" ? "text-destructive" : directionColors[entry.direction]}`}
						>
							<span className="text-muted-foreground shrink-0 tabular-nums">
								{formatLogTime(entry.timestamp)}
							</span>
							<span className="shrink-0">
								{directionArrows[entry.direction]}
							</span>
							<span className="break-all">{entry.raw}</span>
						</div>
					))
				)}
			</div>
		</div>
	);
}

function PinoutPanel({ info }: { info: InfoMessage }) {
	const pinRows: Array<{ fn: string; gpio: number; notes?: string }> = [];

	for (let i = 0; i < info.pins.lane_sensors.length; i++) {
		pinRows.push({
			fn: `Lane ${i + 1} Sensor`,
			gpio: info.pins.lane_sensors[i],
		});
	}
	if (info.pins.servo != null)
		pinRows.push({ fn: "Servo PWM", gpio: info.pins.servo });
	if (info.pins.gate_switch != null)
		pinRows.push({ fn: "Gate Switch", gpio: info.pins.gate_switch });
	if (info.pins.start_button != null)
		pinRows.push({ fn: "Start Button", gpio: info.pins.start_button });
	if (info.pins.reset_button != null)
		pinRows.push({ fn: "Reset Button", gpio: info.pins.reset_button });
	if (info.pins.status_led != null)
		pinRows.push({ fn: "Status LED", gpio: info.pins.status_led });

	return (
		<div className="bg-card rounded-xl p-4 space-y-3">
			<div className="flex items-center justify-between">
				<h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
					Pinout
				</h2>
				<span className="text-xs text-muted-foreground">
					{info.firmware}
					{info.version ? ` v${info.version}` : ""}
				</span>
			</div>

			<table className="w-full text-sm">
				<thead>
					<tr className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
						<th className="text-left pb-1">Function</th>
						<th className="text-left pb-1">GPIO</th>
					</tr>
				</thead>
				<tbody>
					{pinRows.map((row) => (
						<tr
							key={row.fn}
							className="border-t border-secondary/50"
						>
							<td className="py-1.5">{row.fn}</td>
							<td className="py-1.5 font-mono tabular-nums">
								{row.gpio}
							</td>
						</tr>
					))}
				</tbody>
			</table>

			{info.servo && (
				<div className="pt-1 border-t border-secondary/50">
					<p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
						Servo
					</p>
					<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
						<span>
							Model:{" "}
							<span className="font-bold">
								{info.servo.model}
							</span>
						</span>
						<span>
							Closed:{" "}
							<span className="font-bold tabular-nums">
								{info.servo.closed_us}µs
							</span>
						</span>
						<span>
							Open:{" "}
							<span className="font-bold tabular-nums">
								{info.servo.open_us}µs
							</span>
						</span>
						<span>
							Freq:{" "}
							<span className="font-bold tabular-nums">
								{info.servo.freq_hz}Hz
							</span>
						</span>
					</div>
				</div>
			)}

			{info.ble && (
				<div className="pt-1 border-t border-secondary/50">
					<p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
						BLE
					</p>
					<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
						<span>
							Name:{" "}
							<span className="font-bold">{info.ble.name}</span>
						</span>
						<span className="text-xs text-muted-foreground font-mono break-all">
							{info.ble.service_uuid}
						</span>
					</div>
				</div>
			)}
		</div>
	);
}

// ── Helpers ─────────────────────────────────────────────────────

function formatElapsed(ms: number): string {
	return (ms / 1000).toFixed(3);
}

function formatLaneTime(
	result: { elapsed_ms: number | null; status: string } | undefined,
): string {
	if (!result || result.status === "pending") return "--";
	if (result.status === "dnf") return "DNF";
	return `${((result.elapsed_ms ?? 0) / 1000).toFixed(3)}s`;
}

function formatLogTime(ts: number): string {
	const d = new Date(ts);
	return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}.${d.getMilliseconds().toString().padStart(3, "0")}`;
}

function getPlace(
	lane: number,
	heatResults: Array<{
		lane: number;
		elapsed_ms: number;
		status: "valid" | "dnf";
	}> | null,
): number | null {
	if (!heatResults) return null;
	const sorted = [...heatResults]
		.filter((r) => r.status === "valid")
		.sort((a, b) => a.elapsed_ms - b.elapsed_ms);
	const idx = sorted.findIndex((r) => r.lane === lane);
	if (idx === -1) return null;
	return idx + 1;
}

function RemoteCameraPanel({
	sessionCode,
	transport,
}: {
	sessionCode: string;
	transport: import("@/lib/timer/remote-camera-transport").RemoteCameraTransport | null;
}) {
	const [phoneAlive, setPhoneAlive] = useState(false);
	const [phoneStale, setPhoneStale] = useState(false);

	useEffect(() => {
		if (!transport) return;
		const interval = setInterval(() => {
			setPhoneAlive(transport.phoneConnected);
			if (transport.phoneConnected && transport.lastPhoneHeartbeat > 0) {
				const age = Date.now() - transport.lastPhoneHeartbeat;
				setPhoneStale(age > 10_000);
			}
		}, 1000);
		return () => clearInterval(interval);
	}, [transport]);

	const origin = typeof window !== "undefined" ? window.location.origin : "";

	return (
		<div className="bg-card rounded-xl p-4 space-y-3">
			<div className="text-center space-y-2">
				<div className="flex items-center justify-center gap-2">
					<p className="text-sm font-bold text-foreground uppercase tracking-wide">
						Phone Camera Sensor
					</p>
					{phoneAlive && !phoneStale && (
						<span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-success/20 text-success font-bold">
							<span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
							Phone Connected
						</span>
					)}
					{phoneAlive && phoneStale && (
						<span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-status-finals/20 text-status-finals font-bold">
							<span className="w-1.5 h-1.5 rounded-full bg-status-finals" />
							Phone Offline?
						</span>
					)}
				</div>
				{!phoneAlive && (
					<p className="text-xs text-muted-foreground">
						Open this URL on the phone mounted at the finish line:
					</p>
				)}
				<div className="flex items-center justify-center gap-4">
					<QRCodeSVG
						value={`${origin}/sensor?s=${sessionCode}`}
						size={120}
						bgColor="transparent"
						fgColor="currentColor"
						className="text-foreground"
					/>
					<div className="text-left space-y-1">
						<p className="text-2xl font-bold text-primary tracking-widest font-mono">
							{sessionCode}
						</p>
						<p className="text-xs text-muted-foreground break-all">
							{origin}/sensor?s={sessionCode}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
